import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend } from 'recharts';
import api from '../api/client';
import { Link } from 'react-router-dom';
import { Spinner, ErrorBox, Status, Risk, DataTable, num, money, fmtDate } from '../components/ui';

const STATE_COLOURS = {
  'Not started': '#5a6b7a', Outstanding: '#9a7a2e', Started: '#28607f',
  'In progress': '#2a7d8c', Delayed: '#a85a44', Completed: '#3f7d54',
  'Not applicable': '#8a94a0'
};
const TYPE_COLOURS = { HRB: '#213547', LRB: '#356b48', Mixed: '#5a4570' };

function Tile({ label, value, sub, tone, to }) {
  const nav = useNavigate();
  const inner = <>
    <div className="label">{label}</div>
    <div className="value">{num(value)}</div>
    {sub ? <div className="sub">{sub}</div> : null}
  </>;
  return to
    ? <div className={`stat${tone ? ' ' + tone : ''}`} style={{ cursor: 'pointer' }} onClick={() => nav(to)}>{inner}</div>
    : <div className={`stat static${tone ? ' ' + tone : ''}`}>{inner}</div>;
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const nav = useNavigate();
  const load = () => api.get('/dashboard').then(r => setD(r.data)).catch(setErr);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = () => { setD(null); load(); };
    window.addEventListener('bsp2-prog-change', h);
    return () => window.removeEventListener('bsp2-prog-change', h);
  }, []);
  if (err) return <ErrorBox error={err} />;
  if (!d) return <Spinner label="Building dashboard…" />;

  const k = d.kpis, t = d.tasks;
  const buildingData = [
    { name: 'HRB', value: Number(d.buildings.hrb) || 0 },
    { name: 'LRB', value: Number(d.buildings.lrb) || 0 }
  ];
  // One bar per automated status, in lifecycle order.
  const taskData = (d.stateSplit || []).map(x => ({
    name: x.name, value: Number(x.value) || 0, fill: STATE_COLOURS[x.name] || '#888'
  }));

  return <>
    <div className="phead"><h1>Dashboard</h1><span className="muted">At a glance — tap a card or chart to drill in</span></div>

    <div className="stat-grid">
      <Tile label="Programmes" value={k.programmes} sub={`${num(k.projects)} projects (sites)`} to="/programmes" />
      <div className="stat" style={{ cursor: 'pointer', borderLeftColor: '#213547' }} onClick={() => nav('/reports/buildings-register')}>
        <div className="label">Buildings</div>
        <div className="value">{num(d.buildings.total)}</div>
        <div className="sub"><span style={{ color: TYPE_COLOURS.HRB, fontWeight: 600 }}>{num(d.buildings.hrb)} HRB</span> · <span style={{ color: TYPE_COLOURS.LRB, fontWeight: 600 }}>{num(d.buildings.lrb)} LRB</span></div>
      </div>
      <Tile label="Total tasks" value={t.total} sub="all applicable" to="/reports/tasks" />
      <Tile label="Not started" value={t.not_started} sub="start date not reached" to="/reports/tasks?status=Not started" />
      <Tile label="Outstanding" value={t.outstanding} sub="start date passed, not begun" tone="soon" to="/reports/tasks?status=Outstanding" />
      <Tile label="In progress" value={t.in_progress} sub="under way" to="/reports/tasks?status=In progress" />
      <Tile label="Delayed" value={t.delayed} sub="past the expected end date" tone="over" to="/reports/tasks?status=Delayed" />
      <Tile label="Completed" value={t.complete} sub="done" tone="complete" to="/reports/tasks?status=Completed" />
      <Tile label="Due soon" value={t.due_soon} sub="finishing within 28 days" tone="soon" to="/reports/tasks?status=Due soon" />
      <Tile label="Projects at risk" value={(Number(d.risk?.at_risk)||0)+(Number(d.risk?.past_end_date)||0)}
        sub={`${num(d.risk?.at_risk)} approaching · ${num(d.risk?.past_end_date)} past end date`}
        tone="over" to="/reports/projects-at-risk" />
      <div className="stat" style={{cursor:'pointer'}} onClick={() => nav('/budget')}>
        <div className="label">Budget spent</div>
        <div className="value" style={{fontSize:19}}>{money(d.budget?.total_spent)}</div>
        <div className="sub">of {money(d.budget?.budget_amount)} budgeted</div>
      </div>
    </div>

    {/* Projects whose expected end date is approaching or already gone. */}
    {d.atRiskProjects && d.atRiskProjects.length > 0 && (
      <div className="panel">
        <div className="phead"><h3 style={{margin:0}}>Projects at risk</h3>
          <Link to="/reports/projects-at-risk">Full report</Link></div>
        <DataTable rows={d.atRiskProjects} columns={[
          { key:'project_name', label:'Project', render:(v,r)=><Link to={`/projects/${r.project_id}`}>{v}</Link> },
          { key:'programme_name', label:'Programme' },
          { key:'expected_end_date', label:'Expected end date', render:fmtDate },
          { key:'days_to_expected_end', label:'Days left',
            render:v=>v==null?'—':Number(v)<0?<span className="neg">{Math.abs(v)} past</span>:num(v) },
          { key:'risk_level', label:'Risk', render:v=><Risk value={v}/> },
          { key:'progress_status', label:'Status', render:v=><Status value={v}/> },
          { key:'progress', label:'Progress', render:v=>`${Number(v||0).toFixed(0)}%` }
        ]} />
      </div>
    )}

    <div className="chart-grid">
      <div className="chart-box" style={{ cursor: 'pointer' }} onClick={() => nav('/reports/buildings-register')}>
        <h3>Buildings — HRB vs LRB</h3>
        <ResponsiveContainer width="100%" height={230}>
          <PieChart>
            <Pie data={buildingData} dataKey="value" nameKey="name" innerRadius={52} outerRadius={82} paddingAngle={2} label={e => `${e.name} ${e.value}`}>
              {buildingData.map(e => <Cell key={e.name} fill={TYPE_COLOURS[e.name]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-box" style={{ cursor: 'pointer' }} onClick={() => nav('/reports/outstanding-work')}>
        <h3>Tasks by status</h3>
        <ResponsiveContainer width="100%" height={230}>
          <BarChart data={taskData} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" fontSize={11} />
            <YAxis type="category" dataKey="name" width={92} fontSize={11} />
            <Tooltip />
            <Bar dataKey="value">{taskData.map(e => <Cell key={e.name} fill={e.fill} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-box">
        <h3>Projects by building type</h3>
        <ResponsiveContainer width="100%" height={230}>
          <PieChart>
            <Pie data={d.typeSplit} dataKey="value" nameKey="name" outerRadius={82} label>
              {d.typeSplit.map(e => <Cell key={e.name} fill={TYPE_COLOURS[e.name] || '#888'} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>

    <div className="chart-grid">
      <div className="chart-box" style={{ gridColumn: '1 / -1' }}>
        <h3>Stage completion across the portfolio (complete vs outstanding)</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={d.stageProgress} margin={{ bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="stage_name" fontSize={10} angle={-30} textAnchor="end" interval={0} height={70} />
            <YAxis fontSize={11} />
            <Tooltip />
            <Legend />
            <Bar dataKey="complete" stackId="a" fill={STATE_COLOURS.Completed} name="Completed" />
            <Bar dataKey="outstanding" stackId="a" fill={STATE_COLOURS['Not started']} name="Outstanding" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>

    <div className="chart-grid">
      <div className="chart-box" style={{ gridColumn: '1 / -1' }}>
        <h3>Programme progress</h3>
        <ResponsiveContainer width="100%" height={Math.max(180, d.programmeProgress.length * 46)}>
          <BarChart data={d.programmeProgress} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" domain={[0, 100]} fontSize={11} unit="%" />
            <YAxis type="category" dataKey="name" width={200} fontSize={11} />
            <Tooltip formatter={v => `${v}%`} />
            <Bar dataKey="progress" fill="#2a7d8c" name="Progress" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  </>;
}
